import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Cookies from 'js-cookie';
import { gsap } from 'gsap';

const translations = {
  en: {
    candidate: "Candidate",
    reports: "Reports",
    reportList: "Report List",
    filterByDate: "Filter by Date:",
    newestFirst: "Newest First",
    oldestFirst: "Oldest First",
    name: "Name",
    dateGenerated: "Date Generated",
    status: "Status",
    actions: "Actions",
    loading: "Loading reports...",
    noReports: "No Reports Yet",
    completed: "Completed",
    view: "View"
  },
  nl: {
    candidate: "Kandidaat",
    reports: "Rapporten",
    reportList: "Rapportenlijst",
    filterByDate: "Filter op datum:",
    newestFirst: "Nieuwste eerst",
    oldestFirst: "Oudste eerst",
    name: "Naam",
    dateGenerated: "Datum gegenereerd",
    status: "Status",
    actions: "Acties",
    loading: "Rapporten laden...",
    noReports: "Nog geen rapporten",
    completed: "Voltooid",
    view: "Bekijken"
  }
};

function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterByDate, setFilterByDate] = useState('newestFirst');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Language detection
  const [isDutch, setIsDutch] = useState(Cookies.get('isDutch') === 'true');
  const t = translations[isDutch ? 'nl' : 'en'];

  useEffect(() => {
    const interval = setInterval(() => {
      const cookieIsDutch = Cookies.get('isDutch') === 'true';
      if (cookieIsDutch !== isDutch) setIsDutch(cookieIsDutch);
    }, 1000);
    return () => clearInterval(interval);
  }, [isDutch]);

  const fetchReports = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report/all/new`, {
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }

      const data = await response.json();
      if (data.successful) {
        const sortedReports = [...data.data.data].sort((a, b) => {
          const dateA = new Date(a.datetimeInserted);
          const dateB = new Date(b.datetimeInserted);
          return dateB - dateA; // Default to newest first
        });
        setReports(sortedReports);
      } else {
        setError(data.error || 'Failed to fetch reports');
      }

      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDelete = async (reportId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report/new/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete report');
      }

      // Remove the deleted report from the state
      setReports(reports.filter(report => report.id !== reportId));
      fetchReports();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (reports.length > 0) {
      const sortedReports = [...reports].sort((a, b) => {
        const dateA = new Date(a.datetimeInserted);
        const dateB = new Date(b.datetimeInserted);
        return filterByDate === 'newestFirst' 
          ? dateB - dateA 
          : dateA - dateB;
      });
      setReports(sortedReports);
    }
  }, [filterByDate]);

  useEffect(() => {
   if(reports){
    // Helper functions for GSAP performance optimization
    const setGSAPProperties = (element, props) => {
      if (props.force3D) {
        element.style.transform = 'translate3d(0, 0, 0)';
      }
      if (props.willChange) {
        element.style.willChange = props.willChange;
      }
    };

    const resetGSAPProperties = (element) => {
      element.style.willChange = 'auto';
    };

    // Direct fadeUp animation
    const initFadeUp = () => {
      const fadeUpElements = document.querySelectorAll(".fadeUp");

      if (fadeUpElements.length === 0) {
        setTimeout(initFadeUp, 100);
        return;
      }

      // Set CSS properties directly for better performance
      fadeUpElements.forEach(element => {
        setGSAPProperties(element, { force3D: true, willChange: "transform, opacity" });
      });

      const animationDelay = 0; // Base delay in milliseconds

      gsap.set(fadeUpElements, { 
        opacity: 0, 
        y: 20
      });
      
      gsap.to(fadeUpElements, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        delay: 0.2 + (animationDelay / 1000), // Add navbar delay to base delay
        stagger: 0.1,
        ease: "power2.out",
        onComplete: () => {
          // Reset CSS properties after animation
          fadeUpElements.forEach(element => {
            resetGSAPProperties(element);
          });
        }
      });
    };

    initFadeUp();
   }
}, [reports]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="m-0 text-left">
      <h1 className="text-5xl font-bold mb-4 text-primary">{t.reports}</h1>
      <main className="flex flex-col gap-4">
      <div className="flex justify-between gap-2 items-center mt-8">
        <h2 className="text-2xl font-bold">{t.reportList}</h2>
        <div className="flex items-center gap-4">
          <span>{t.filterByDate}</span>
          <div ref={dropdownRef} className="relative">
            <div
              className="border p-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-gray-50"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span>{filterByDate === 'newestFirst' ? t.newestFirst : t.oldestFirst}</span>
              <svg
                className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            
            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded-md shadow-lg z-10">
                <div
                  className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                  onClick={() => {
                    setFilterByDate('newestFirst');
                    setIsDropdownOpen(false);
                  }}
                >
                  {t.newestFirst}
                </div>
                <div
                  className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                  onClick={() => {
                    setFilterByDate('oldestFirst');
                    setIsDropdownOpen(false);
                  }}
                >
                  {t.oldestFirst}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2">{t.name}</th>
              <th className="border border-gray-300 p-2">{t.dateGenerated}</th>
              <th className="border border-gray-300 p-2">{t.status}</th>
              <th className="border border-gray-300 p-2">{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="4" className="border border-gray-300 p-2">{t.loading}</td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan="4" className="border border-gray-300 p-2">{t.noReports}</td>
              </tr>
            )}
            {reports.map((report) => (
              <tr key={report.id} className="fadeUp">
                <td className="border border-gray-300 p-2">{report.linkedInData.firstName ? `${report.linkedInData.firstName} ${report.linkedInData.lastName}` : t.candidate}</td>
                <td className="border border-gray-300 p-2">{new Date(report.datetimeInserted).toLocaleDateString()}</td>
                <td className="border border-gray-300 p-2">{t.completed}</td>
                <td className="border border-gray-300 p-2">
                  <div className="flex gap-2">
                    <button className="border bg-default text-white px-4 py-2 rounded-md"
                    onClick={() => navigate(`/reports/${report.id}`)}
                    >{t.view}</button>
                      <button className="border px-4 py-2 rounded-md"
                    onClick={() => handleDelete(report.id)}
                    >Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}

export default Reports; 